import {useId, useState} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {SearchRepeatIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {usePersonalizedHomepageImpression} from "../../hooks/use_personalized_homepage_impression";
import {IPersonalizedRecentSearch} from "../../types/IPersonalizedHomepage";

interface IProps {
    searches: IPersonalizedRecentSearch[];
    isLoading?: boolean;
    className?: string;
    maxItems?: number;
    onView?: (searches: IPersonalizedRecentSearch[], startIndex: number) => void;
    onShowMoreClick?: (label: string) => void;
    onRecentSearchClick?: (search: IPersonalizedRecentSearch, index: number) => void;
}

const SHOW_MORE_LABEL = "Pokaż więcej";
const RECENT_SEARCHES_SKELETON_ITEMS = [0, 1, 2, 3];

export const PersonalizedHomepageRecentSearches = (props: IProps) => {
    const {searches, isLoading = false, className, maxItems = 4, onView, onShowMoreClick, onRecentSearchClick} = props;
    const headingId = useId();
    const listId = useId();
    const [visibleCount, setVisibleCount] = useState(maxItems);
    const visibleSearches = searches.slice(0, visibleCount);

    if (!isLoading && visibleSearches.length === 0) {
        return null;
    }

    return (
        <section className={cx(recentSearchesStyle, className)} aria-labelledby={headingId} aria-busy={isLoading}>
            <Text id={headingId} as="h2" variant="info_txt_2" className={recentSearchesHeadingStyle}>
                Ostatnio wyszukiwane:
            </Text>

            <div id={listId} className={recentSearchesBatchesStyle}>
                {isLoading ? (
                    <ul className={recentSearchesListStyle}>
                        {RECENT_SEARCHES_SKELETON_ITEMS.map((item) => (
                            <li key={item} className={recentSearchesItemStyle}>
                                <span className={recentSearchSkeletonStyle} />
                            </li>
                        ))}
                    </ul>
                ) : (
                    Array.from({length: Math.ceil(visibleSearches.length / maxItems)}, (_, batchIndex) => (
                        <PersonalizedHomepageRecentSearchesBatch
                            key={batchIndex}
                            searches={visibleSearches.slice(batchIndex * maxItems, (batchIndex + 1) * maxItems)}
                            startIndex={batchIndex * maxItems}
                            onView={onView}
                            onRecentSearchClick={onRecentSearchClick}
                        />
                    ))
                )}
            </div>

            {!isLoading && visibleCount < searches.length ? (
                <Button
                    type="button"
                    variant="outlined_secondary_light"
                    size="small"
                    textVariant="body_copy_2"
                    className={showMoreStyle}
                    aria-controls={listId}
                    onClick={() => {
                        onShowMoreClick?.(SHOW_MORE_LABEL);
                        setVisibleCount((count) => count + maxItems);
                    }}
                >
                    {SHOW_MORE_LABEL}
                </Button>
            ) : null}
        </section>
    );
};

const PersonalizedHomepageRecentSearchesBatch = ({
    searches,
    startIndex,
    onView,
    onRecentSearchClick
}: Pick<IProps, "searches" | "onView" | "onRecentSearchClick"> & {startIndex: number}) => {
    const batchRef = usePersonalizedHomepageImpression<HTMLUListElement>({
        isEnabled: !!onView,
        onImpression: () => onView?.(searches, startIndex)
    });

    return (
        <ul className={recentSearchesListStyle} ref={batchRef}>
            {searches.map((search, index) => (
                <li key={search.id} className={recentSearchesItemStyle}>
                    <a
                        className={recentSearchStyle}
                        href={search.href}
                        title={`${search.title} — ${search.description}`}
                        onClick={() => onRecentSearchClick?.(search, startIndex + index)}
                    >
                        <span className={recentSearchCopyStyle}>
                            <span className={recentSearchTitleRowStyle}>
                                <Text as="span" variant="body_copy_2" className={recentSearchTitleStyle}>
                                    {search.title}
                                </Text>
                                {search.newOffersLabel ? (
                                    <Text as="span" variant="info_txt_3" className={recentSearchNewPillStyle}>
                                        {search.newOffersLabel}
                                    </Text>
                                ) : null}
                            </span>
                            <Text as="span" variant="info_txt_2" className={recentSearchDescriptionStyle}>
                                {search.description}
                            </Text>
                        </span>

                        <span className={recentSearchIconStyle}>
                            <SearchRepeatIcon size="2.4" fill="var(--colors-white)" />
                        </span>
                    </a>
                </li>
            ))}
        </ul>
    );
};

const recentSearchesStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    width: "100%"
});

const recentSearchesHeadingStyle = css({
    display: "none",
    margin: 0,
    color: "color-mix(in srgb, var(--colors-white) 80%, transparent)",
    fontWeight: 400,

    md: {
        display: "block"
    }
});

const recentSearchesListStyle = css({
    display: "flex",
    gap: 1,
    margin: 0,
    padding: 0,
    listStyle: "none",
    overflowX: "auto",
    scrollbarWidth: "none",
    scrollSnapType: "x mandatory",

    "&::-webkit-scrollbar": {
        display: "none"
    },

    md: {
        overflowX: "visible"
    }
});

const recentSearchesItemStyle = css({
    flex: "0 0 auto",
    width: "min(32rem, 100%)",
    scrollSnapAlign: "start",

    md: {
        flex: "1 1 0",
        width: "auto",
        minWidth: 0
    }
});

const recentSearchStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 2,
    boxSizing: "border-box",
    width: "100%",
    height: "auto",
    py: 1,
    pr: 1,
    pl: "1.2rem",
    color: "var(--colors-white)",
    backgroundColor: "color-mix(in srgb, var(--colors-secondary) 58%, transparent)",
    borderRadius: 12,
    backdropFilter: "blur(4px)",
    textDecoration: "none",
    transition: "background-color 160ms ease",

    md: {
        height: "6.4rem",
        pt: 1.5,
        pr: 1.5,
        pb: 1.5,
        pl: 2
    },

    _hover: {
        color: "var(--colors-white)",
        backgroundColor: "color-mix(in srgb, var(--colors-secondary) 76%, transparent)",
        textDecoration: "none"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    }
});

const recentSearchSkeletonStyle = css({
    display: "block",
    boxSizing: "border-box",
    width: "100%",
    height: "5.6rem",
    backgroundColor: "color-mix(in srgb, var(--colors-secondary) 40%, transparent)",
    borderRadius: 12,
    backdropFilter: "blur(4px)",

    md: {
        height: "6.4rem"
    }
});

const recentSearchCopyStyle = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0",
    gap: 1,
    minWidth: 0
});

const recentSearchTitleRowStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    minWidth: 0
});

const recentSearchTitleStyle = css({
    overflow: "hidden",
    color: "var(--colors-white)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const recentSearchNewPillStyle = css({
    flex: "0 0 auto",
    px: 1,
    py: 0.5,
    color: "var(--colors-secondary)",
    backgroundColor: "var(--colors-success)",
    borderRadius: 999,
    whiteSpace: "nowrap"
});

const recentSearchDescriptionStyle = css({
    overflow: "hidden",
    color: "var(--colors-white)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const recentSearchIconStyle = css({
    display: "flex",
    flex: "0 0 auto",
    alignItems: "center",
    justifyContent: "center",
    padding: 1,
    backgroundColor: "color-mix(in srgb, var(--colors-white) 10%, transparent)",
    borderRadius: 1,
    backdropFilter: "blur(4px)"
});

const recentSearchesBatchesStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const showMoreStyle = css({
    alignSelf: "flex-end"
});
import {useId, useState} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {SearchRepeatIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";

import {usePersonalizedHomepageImpression} from "../../hooks/use_personalized_homepage_impression";
import {IPersonalizedRecentSearch} from "../../types/IPersonalizedHomepage";

interface IProps {
    searches: IPersonalizedRecentSearch[];
    isLoading?: boolean;
    className?: string;
    maxItems?: number;
    onView?: (searches: IPersonalizedRecentSearch[], startIndex: number) => void;
    onShowMoreClick?: (label: string) => void;
    onRecentSearchClick?: (search: IPersonalizedRecentSearch, index: number) => void;
}

const SHOW_MORE_LABEL = "Pokaż więcej";
const RECENT_SEARCHES_SKELETON_ITEMS = [0, 1, 2, 3];

export const PersonalizedHomepageRecentSearches = (props: IProps) => {
    const {searches, isLoading = false, className, maxItems = 4, onView, onShowMoreClick, onRecentSearchClick} = props;
    const headingId = useId();
    const listId = useId();
    const [visibleCount, setVisibleCount] = useState(maxItems);
    const visibleSearches = searches.slice(0, visibleCount);

    if (!isLoading && visibleSearches.length === 0) {
        return null;
    }

    return (
        <section className={cx(recentSearchesStyle, className)} aria-labelledby={headingId} aria-busy={isLoading}>
            <Text id={headingId} as="h2" variant="info_txt_2" className={recentSearchesHeadingStyle}>
                Ostatnio wyszukiwane:
            </Text>

            <div id={listId} className={recentSearchesBatchesStyle}>
                {isLoading ? (
                    <ul className={recentSearchesListStyle}>
                        {RECENT_SEARCHES_SKELETON_ITEMS.map((item) => (
                            <li key={item} className={recentSearchesItemStyle}>
                                <span className={recentSearchSkeletonStyle} />
                            </li>
                        ))}
                    </ul>
                ) : (
                    Array.from({length: Math.ceil(visibleSearches.length / maxItems)}, (_, batchIndex) => (
                        <PersonalizedHomepageRecentSearchesBatch
                            key={batchIndex}
                            searches={visibleSearches.slice(batchIndex * maxItems, (batchIndex + 1) * maxItems)}
                            startIndex={batchIndex * maxItems}
                            onView={onView}
                            onRecentSearchClick={onRecentSearchClick}
                        />
                    ))
                )}
            </div>

            {!isLoading && visibleCount < searches.length ? (
                <Button
                    type="button"
                    variant="outlined_secondary_light"
                    size="small"
                    textVariant="body_copy_2"
                    className={showMoreStyle}
                    aria-controls={listId}
                    onClick={() => {
                        onShowMoreClick?.(SHOW_MORE_LABEL);
                        setVisibleCount((count) => count + maxItems);
                    }}
                >
                    {SHOW_MORE_LABEL}
                </Button>
            ) : null}
        </section>
    );
};

const PersonalizedHomepageRecentSearchesBatch = ({
    searches,
    startIndex,
    onView,
    onRecentSearchClick
}: Pick<IProps, "searches" | "onView" | "onRecentSearchClick"> & {startIndex: number}) => {
    const batchRef = usePersonalizedHomepageImpression<HTMLUListElement>({
        isEnabled: !!onView,
        onImpression: () => onView?.(searches, startIndex)
    });

    return (
        <ul className={recentSearchesListStyle} ref={batchRef}>
            {searches.map((search, index) => (
                <li key={search.id} className={recentSearchesItemStyle}>
                    <a
                        className={recentSearchStyle}
                        href={search.href}
                        title={`${search.title} — ${search.description}`}
                        onClick={() => onRecentSearchClick?.(search, startIndex + index)}
                    >
                        <span className={recentSearchCopyStyle}>
                            <span className={recentSearchTitleRowStyle}>
                                <Text as="span" variant="body_copy_2" className={recentSearchTitleStyle}>
                                    {search.title}
                                </Text>
                                {search.newOffersLabel ? (
                                    <Text as="span" variant="info_txt_3" className={recentSearchNewPillStyle}>
                                        {search.newOffersLabel}
                                    </Text>
                                ) : null}
                            </span>
                            <Text as="span" variant="info_txt_2" className={recentSearchDescriptionStyle}>
                                {search.description}
                            </Text>
                        </span>

                        <span className={recentSearchIconStyle}>
                            <SearchRepeatIcon size="2.4" fill="var(--colors-white)" />
                        </span>
                    </a>
                </li>
            ))}
        </ul>
    );
};

const recentSearchesStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    width: "100%"
});

const recentSearchesHeadingStyle = css({
    display: "none",
    margin: 0,
    color: "color-mix(in srgb, var(--colors-white) 80%, transparent)",
    fontWeight: 400,

    md: {
        display: "block"
    }
});

const recentSearchesListStyle = css({
    display: "flex",
    gap: 1,
    margin: 0,
    padding: 0,
    listStyle: "none",
    overflowX: "auto",
    scrollbarWidth: "none",
    scrollSnapType: "x mandatory",

    "&::-webkit-scrollbar": {
        display: "none"
    },

    md: {
        overflowX: "visible"
    }
});

const recentSearchesItemStyle = css({
    flex: "0 0 auto",
    width: "min(32rem, 100%)",
    scrollSnapAlign: "start",

    md: {
        flex: "1 1 0",
        width: "auto",
        minWidth: 0
    }
});

const recentSearchStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 2,
    boxSizing: "border-box",
    width: "100%",
    height: "auto",
    py: 1,
    pr: 1,
    pl: "1.2rem",
    color: "var(--colors-white)",
    backgroundColor: "color-mix(in srgb, var(--colors-secondary) 58%, transparent)",
    borderRadius: 12,
    backdropFilter: "blur(4px)",
    textDecoration: "none",
    transition: "background-color 160ms ease",

    md: {
        height: "6.4rem",
        pt: 1.5,
        pr: 1.5,
        pb: 1.5,
        pl: 2
    },

    _hover: {
        color: "var(--colors-white)",
        backgroundColor: "color-mix(in srgb, var(--colors-secondary) 76%, transparent)",
        textDecoration: "none"
    },

    _focusVisible: {
        outline: "2px solid var(--colors-primary)",
        outlineOffset: "2px"
    }
});

const recentSearchSkeletonStyle = css({
    display: "block",
    boxSizing: "border-box",
    width: "100%",
    height: "5.6rem",
    backgroundColor: "color-mix(in srgb, var(--colors-secondary) 40%, transparent)",
    borderRadius: 12,
    backdropFilter: "blur(4px)",

    md: {
        height: "6.4rem"
    }
});

const recentSearchCopyStyle = css({
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0",
    gap: 1,
    minWidth: 0
});

const recentSearchTitleRowStyle = css({
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    minWidth: 0
});

const recentSearchTitleStyle = css({
    overflow: "hidden",
    color: "var(--colors-white)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const recentSearchNewPillStyle = css({
    flex: "0 0 auto",
    px: 1,
    py: 0.5,
    color: "var(--colors-secondary)",
    backgroundColor: "var(--colors-success)",
    borderRadius: 999,
    whiteSpace: "nowrap"
});

const recentSearchDescriptionStyle = css({
    overflow: "hidden",
    color: "var(--colors-white)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const recentSearchIconStyle = css({
    display: "flex",
    flex: "0 0 auto",
    alignItems: "center",
    justifyContent: "center",
    padding: 1,
    backgroundColor: "color-mix(in srgb, var(--colors-white) 10%, transparent)",
    borderRadius: 1,
    backdropFilter: "blur(4px)"
});

const recentSearchesBatchesStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const showMoreStyle = css({
    alignSelf: "flex-end"
});
